import React, { useState, useEffect } from 'react';
import { siteVisitAPI, leadAPI, projectAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import { 
  MapPin, Calendar as CalendarIcon, Clock, User, Check, X, 
  Plus, Phone, Building2, AlertCircle, CheckCircle2, XCircle
} from 'lucide-react';

const SiteVisitsPage = () => {
  const { hasRole } = useAuth();
  const [visits, setVisits] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState({ open: false, visitId: null });
  const [activeTab, setActiveTab] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);

  const [newVisit, setNewVisit] = useState({
    project_id: '',
    lead_id: '',
    scheduled_date: null,
    scheduled_time: '',
  });

  const [feedback, setFeedback] = useState('');

  const canManage = hasRole(['builder_admin', 'sales_team']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [visitsRes, leadsRes, projectsRes] = await Promise.all([
        siteVisitAPI.list(),
        leadAPI.list(),
        projectAPI.list(),
      ]);
      setVisits(visitsRes.data);
      setLeads(leadsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVisit = async (e) => {
    e.preventDefault();
    if (!newVisit.scheduled_date) {
      toast.error('Please select a date');
      return;
    }
    try {
      await siteVisitAPI.create({
        ...newVisit,
        scheduled_date: newVisit.scheduled_date.toISOString(),
      });
      toast.success('Site visit scheduled! Sales team has been notified.');
      setDialogOpen(false);
      setNewVisit({ project_id: '', lead_id: '', scheduled_date: null, scheduled_time: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule visit');
    }
  };

  const handleComplete = async () => {
    try {
      await siteVisitAPI.complete(feedbackDialog.visitId, feedback);
      toast.success('Site visit marked as completed');
      setFeedbackDialog({ open: false, visitId: null });
      setFeedback('');
      loadData();
    } catch (error) {
      toast.error('Failed to complete visit');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'scheduled': { class: 'bg-blue-100 text-blue-700', icon: Clock },
      'completed': { class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
      'cancelled': { class: 'bg-red-100 text-red-700', icon: XCircle },
      'no_show': { class: 'bg-orange-100 text-orange-700', icon: AlertCircle },
    };
    const style = styles[status] || styles.scheduled;
    const Icon = style.icon;
    return (
      <Badge className={style.class}>
        <Icon className="h-3 w-3 mr-1" />
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead ? lead.name : 'Unknown';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown';
  };

  const filteredVisits = visits.filter((v) => {
    const matchesTab = activeTab === 'all' || v.status === activeTab;
    const matchesDate = !selectedDate || 
      new Date(v.scheduled_date).toDateString() === selectedDate.toDateString();
    return matchesTab && matchesDate;
  });

  const todayVisits = visits.filter(v => {
    const visitDate = new Date(v.scheduled_date);
    const today = new Date();
    return visitDate.toDateString() === today.toDateString() && v.status === 'scheduled';
  });

  const stats = {
    total: visits.length,
    scheduled: visits.filter(v => v.status === 'scheduled').length,
    completed: visits.filter(v => v.status === 'completed').length,
    today: todayVisits.length,
  };

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM'
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="site-visits-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Site Visits</h1>
          <p className="text-muted-foreground">Schedule and track property visits</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-schedule-visit">
              <Plus className="h-4 w-4 mr-2" />
              Schedule Visit
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Schedule Site Visit</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateVisit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Project *</Label>
                <Select value={newVisit.project_id} onValueChange={(v) => setNewVisit({...newVisit, project_id: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lead *</Label>
                <Select value={newVisit.lead_id} onValueChange={(v) => setNewVisit({...newVisit, lead_id: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select lead" /></SelectTrigger>
                  <SelectContent>
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} - {l.phone}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newVisit.scheduled_date ? format(newVisit.scheduled_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newVisit.scheduled_date}
                      onSelect={(date) => setNewVisit({...newVisit, scheduled_date: date})}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Time Slot *</Label>
                <Select value={newVisit.scheduled_time} onValueChange={(v) => setNewVisit({...newVisit, scheduled_time: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select time" /></SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Important
                </p>
                <p className="text-blue-700 mt-1">Sales team will be notified immediately. Ensure the client is confirmed before scheduling.</p>
              </div>
              <Button type="submit" className="w-full">Schedule Visit</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <MapPin className="h-4 w-4" />
              <span className="text-sm">Total Visits</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="summary-card border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <CalendarIcon className="h-4 w-4" />
              <span className="text-sm">Today's Visits</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{stats.today}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Scheduled</span>
            </div>
            <p className="text-2xl font-bold">{stats.scheduled}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Completed</span>
            </div>
            <p className="text-2xl font-bold">{stats.completed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Visits Alert */}
      {todayVisits.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5" />
              {todayVisits.length} Visit{todayVisits.length > 1 ? 's' : ''} Scheduled Today
            </h3>
            <div className="space-y-2">
              {todayVisits.map((visit) => (
                <div key={visit.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                  <div>
                    <p className="font-medium">{getLeadName(visit.lead_id)}</p>
                    <p className="text-sm text-muted-foreground">{getProjectName(visit.project_id)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{visit.scheduled_time}</p>
                    {canManage && (
                      <Button size="sm" variant="outline" className="mt-1" onClick={() => setFeedbackDialog({ open: true, visitId: visit.id })}>
                        Mark Complete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate ? format(selectedDate, 'PPP') : 'Filter by date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} />
          </PopoverContent>
        </Popover>
        {selectedDate && (
          <Button variant="ghost" onClick={() => setSelectedDate(null)}>Clear date</Button>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="ml-auto">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="no_show">No Show</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Visits List */}
      {filteredVisits.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No site visits found</p>
            <p className="text-muted-foreground">Schedule a visit to track property showings</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredVisits.map((visit) => (
            <Card key={visit.id} className="summary-card">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{getLeadName(visit.lead_id)}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {getProjectName(visit.project_id)}
                        </p>
                      </div>
                      {getStatusBadge(visit.status)}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground ml-13">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        {format(new Date(visit.scheduled_date), 'PPP')}
                      </span>
                      {visit.scheduled_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {visit.scheduled_time}
                        </span>
                      )}
                    </div>
                    {visit.feedback && (
                      <p className="text-sm mt-2 ml-13 p-2 bg-muted rounded">
                        <span className="font-medium">Feedback:</span> {visit.feedback}
                      </p>
                    )}
                  </div>
                  
                  {canManage && visit.status === 'scheduled' && (
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setFeedbackDialog({ open: true, visitId: visit.id })}>
                        <Check className="h-4 w-4 mr-1" /> Complete
                      </Button>
                      <Button size="sm" variant="outline" className="text-orange-600">
                        No Show
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialog.open} onOpenChange={(open) => setFeedbackDialog({ open, visitId: open ? feedbackDialog.visitId : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Site Visit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Visit Feedback</Label>
              <Textarea 
                value={feedback} 
                onChange={(e) => setFeedback(e.target.value)} 
                placeholder="How did the visit go? Client's interest level, concerns, next steps..."
                rows={4}
              />
            </div>
            <Button onClick={handleComplete} className="w-full">Mark as Completed</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteVisitsPage;
